import { Badge, Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import RawHTMLComponent from "Component/Learning/Common/RawHTML";
import AdminShapePreview from "Component/Learning/BankQuestion/AdminShapePreview";
import { CLICKABLE_LENGTH_BY_TYPE } from "Component/Learning/BankQuestion/questionsForm.helpers";
import formatNumberCompact from "Utils/formatNumberCompact";
import { getInteractiveType, getNonInteractiveQuestionType } from "Utils/getQuestionType";

const TryoutAIAuditQuestionDetailModal = ({ isOpen, onClose, question }) => {
  const choices = Array.isArray(question?.choices) ? question.choices : [];
  const answerKeys = Array.isArray(question?.answer_keys) ? question.answer_keys : [];
  const isInteractiveEnabled =
    question?.is_interactive === 1 ||
    question?.is_interactive === "1" ||
    question?.is_interactive === true;
  const interactiveType = question?.interactive_type;
  const interactiveDetail = Array.isArray(question?.interactive_detail)
    ? question.interactive_detail[0] || {}
    : question?.interactive_detail || {};
  const clickableType = question?.clickable_type ?? interactiveDetail?.clickable_type;
  const clickableCellCount = clickableType ? CLICKABLE_LENGTH_BY_TYPE[clickableType] ?? undefined : undefined;
  const dragpinRoomCount = question?.room_count ?? interactiveDetail?.room_count;
  const dragpinPinCount = question?.pin_count ?? interactiveDetail?.pin_count;
  const dragpinRoomNames = Array.isArray(interactiveDetail?.room_custom_names)
    ? interactiveDetail.room_custom_names.filter(Boolean)
    : [];
  const dragpinPinNames = Array.isArray(interactiveDetail?.pin_custom_names)
    ? interactiveDetail.pin_custom_names.filter(Boolean)
    : [];
  const dragpinPinsMeta = Array.isArray(interactiveDetail?.dragpin_pins)
    ? interactiveDetail.dragpin_pins
    : [];
  const chartStep = interactiveDetail?.step;
  const minMax = interactiveDetail?.minMax;
  const minProportion = interactiveDetail?.min_proportion;

  const getChoiceTextById = (choiceId) => {
    const matchedChoice = choices.find((choice) => String(choice.id) === String(choiceId));
    return matchedChoice?.text ?? matchedChoice?.choice ?? choiceId;
  };

  const getClickablePreviewPreset = () => {
    const candidates = [
      question?.answer_values,
      question?.answer,
      question?.answers,
      question?.interactive_detail?.[0]?.values,
      question?.interactive_detail?.[0]?.preset,
      question?.answer_keys?.[0]?.values,
      question?.answer_keys?.[0]?.resolved_values,
    ];

    for (const candidate of candidates) {
      if (!Array.isArray(candidate)) continue;

      const values = candidate.map((item) => {
        if (typeof item === "number") return item;
        if (typeof item === "string") {
          const parsed = Number(item);
          return Number.isFinite(parsed) ? parsed : 0;
        }
        if (item && typeof item === "object") {
          const parsed = Number(item.value ?? item.choice_id ?? item.id ?? item.answer ?? 0);
          return Number.isFinite(parsed) ? parsed : 0;
        }
        return 0;
      });

      if (values.some((value) => value !== 0)) return values;
    }

    return [];
  };

  const formatDragpinAnswerValue = (value) => {
    if (interactiveType !== "dragpin" || typeof value !== "string") return value;

    const match = value.match(/^\s*Room\s*(\d+)\s*:\s*(.*)\s*$/i);
    if (!match) return value;

    const roomNumber = Number(match[1]);
    const pinNames = (match[2] ?? "").trim();
    const customRoomName = Array.isArray(dragpinRoomNames) && dragpinRoomNames[roomNumber - 1]
      ? dragpinRoomNames[roomNumber - 1].trim()
      : "";
    const roomLabel = customRoomName || `Room ${roomNumber}`;

    return `${roomLabel}:${pinNames}`;
  };

  const renderAnswerValue = (answerKey, index) => {
    const resolvedValues = Array.isArray(answerKey?.resolved_values) ? answerKey.resolved_values : [];

    if (resolvedValues.length > 0) {
      return resolvedValues.map((value, valueIndex) => {
        const displayValue = value?.text ?? value?.choice_id ?? value?.value ?? value ?? "-";
        return (
          <div key={`${answerKey.answer_id ?? index}-${valueIndex}`} className="text-dark mt-1">
            {typeof displayValue === "string" ? formatDragpinAnswerValue(displayValue) : displayValue}
          </div>
        );
      });
    }

    if (Array.isArray(answerKey?.values) && answerKey.values.length > 0) {
      return answerKey.values.map((value, valueIndex) => {
        const displayValue = typeof value === "string" && value.length > 0 ? getChoiceTextById(value) : value;
        return (
          <div key={`${answerKey.answer_id ?? index}-${valueIndex}`} className="text-dark mt-1">
            {typeof displayValue === "string" ? formatDragpinAnswerValue(displayValue) : displayValue}
          </div>
        );
      });
    }

    return <div className="text-dark mt-1">-</div>;
  };

  const renderClickableShapePreview = () => {
    if (!clickableType) return null;

    return (
      <div className="d-flex justify-content-center py-2">
        <div className="w-100" style={{ maxWidth: 320 }}>
          <AdminShapePreview
            variant={clickableType === "triangle" ? "triangle" : clickableType === "boxhard" ? "boxhard" : "box"}
            preset={getClickablePreviewPreset()}
            readOnly
            height={260}
          />
        </div>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>Detail Soal</ModalHeader>
      <ModalBody>
        {question ? (
          <>
            <div className="rounded-4 border p-3 bg-light-subtle">
              <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                <span className="fw-semibold">{question.title ?? "-"}</span>
                <Badge color="primary">{getNonInteractiveQuestionType(question)}</Badge>
                {question.category?.title && <Badge color="secondary">{question.category.title}</Badge>}
                {isInteractiveEnabled && interactiveType && (
                  <Badge color="info">{getInteractiveType(question)}</Badge>
                )}
              </div>
              <div className="text-muted small">
                {question.order ? `Nomor urut: ${question.order}` : "-"}
              </div>
            </div>

            {isInteractiveEnabled && (
              <div className="mt-3">
                <div className="fw-semibold mb-2">Detail Interaktif</div>
                <div className="border rounded p-3 bg-white">
                  <div className="row g-2 small text-muted">
                    <div className="col-md-6">
                      <span className="fw-semibold">Status Interaktif:</span> Ya
                    </div>
                    <div className="col-md-6">
                      <span className="fw-semibold">Tipe Interaktif:</span> {getInteractiveType(question)}
                    </div>
                    {interactiveType === "clickable" && (
                      <>
                        <div className="col-md-6">
                          <span className="fw-semibold">Jumlah Cell:</span>{" "}
                          {clickableCellCount ? formatNumberCompact(clickableCellCount) : "Tidak ada"}
                        </div>
                        <div className="col-12">
                          {renderClickableShapePreview()}
                        </div>
                      </>
                    )}
                    {(interactiveType === "dragpin" || question?.interactive_type === "dragpin") && (
                      <>
                        <div className="col-md-6">
                          <span className="fw-semibold">Jumlah Room:</span>{" "}
                          {dragpinRoomCount ? formatNumberCompact(dragpinRoomCount) : "Tidak ada"}
                        </div>
                        <div className="col-md-6">
                          <span className="fw-semibold">Jumlah Pin:</span>{" "}
                          {dragpinPinCount ? formatNumberCompact(dragpinPinCount) : "Tidak ada"}
                        </div>
                        {dragpinRoomNames.length > 0 && (
                          <div className="col-12">
                            <span className="fw-semibold">Nama Room:</span>{" "}
                            {dragpinRoomNames.join(", ")}
                          </div>
                        )}
                        {dragpinPinNames.length > 0 && (
                          <div className="col-12">
                            <span className="fw-semibold">Nama Pin:</span>{" "}
                            {dragpinPinNames.join(", ")}
                          </div>
                        )}
                      </>
                    )}
                    {(interactiveType === "bar_chart" || interactiveType === "line_chart") && (
                      <>
                        <div className="col-md-6">
                          <span className="fw-semibold">Step:</span>{" "}
                          {chartStep ? formatNumberCompact(chartStep) : "1"}
                        </div>
                        {minMax && (
                          <div className="col-md-6">
                            <span className="fw-semibold">Min/Max:</span>{" "}
                            {Array.isArray(minMax) ? minMax.join(" - ") : minMax}
                          </div>
                        )}
                      </>
                    )}
                    {interactiveType === "pie_chart" && minProportion && (
                      <div className="col-md-6">
                        <span className="fw-semibold">Min Proporsi:</span>{" "}
                        {minProportion}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="mt-3">
              <div className="fw-semibold mb-2">Deskripsi Soal</div>
              <RawHTMLComponent htmlContent={question.description ?? ""} />
            </div>

            {dragpinPinsMeta.length > 0 && (
              <div className="mt-3">
                <div className="fw-semibold mb-2">Deskripsi Pin</div>
                <div className="border rounded p-3 bg-white">
                  {dragpinPinsMeta.map((pin, index) => (
                    <div key={`${pin.name ?? index}`} className="mb-3 last-mb-0">
                      <div className="fw-semibold">{pin.name ?? `Pin ${index + 1}`}</div>
                      <div className="mt-1 text-dark">
                        {pin.label ? (
                          <RawHTMLComponent htmlContent={pin.label} padding={false} />
                        ) : (
                          <span className="text-muted">(Tidak ada deskripsi)</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {choices.length > 0 && (
              <div className="mt-3">
                <div className="fw-semibold mb-2">Pilihan Jawaban</div>
                <div className="list-group">
                  {choices.map((choice, index) => (
                    <div key={choice.id ?? `${choice.text}-${index}`} className="list-group-item">
                      <span className="fw-semibold me-2">{String.fromCharCode(65 + index)}.</span>
                      <span className="text-dark">{choice.text ?? choice.choice ?? "-"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {answerKeys.length > 0 && (
              <div className="mt-3">
                <div className="fw-semibold mb-2">Kunci Jawaban</div>
                <div className="list-group">
                  {answerKeys.map((answerKey, index) => (
                    <div key={answerKey.answer_id ?? `${answerKey.values?.join("-") ?? index}`} className="list-group-item">
                      <div className="fw-semibold">Jawaban</div>
                      {interactiveType === 'clickable' && (
                        <div className="mb-2">
                          {/* clickable value convention: 0=putih, 1=oranye, 2=pattern */}
                          <small>
                            Keterangan nilai: <b>0</b>=Putih, <b>1</b>=Oranye, <b>2</b>=Pola
                          </small>
                        </div>
                      )}
                      {renderAnswerValue(answerKey, index)}
                      {answerKey.explanation && (
                        <div className="mt-2">
                          <div className="text-muted small">Penjelasan</div>
                          <RawHTMLComponent htmlContent={answerKey.explanation} padding={false} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-muted">Detail soal tidak tersedia.</div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onClose}>
          Tutup
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TryoutAIAuditQuestionDetailModal;
